import { useState } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  SidebarLanguageToggle,
  useSidebar,
  useAuth,
  useToast,
} from '@org/ui';
import {
  ChevronRight,
  Compass,
  GripVertical,
  Home,
  Shield,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export function AppSidebar() {
  const { t } = useTranslation('community');
  const { user } = useAuth();
  const location = useLocation();
  const { isMobile, setOpen, setOpenMobile } = useSidebar();
  const { toast } = useToast();

  const isAdmin =
    user?.permissions?.includes('admin:all') || user?.permissions?.includes('*');

  const closeSidebar = () => {
    if (isMobile) {
      setOpenMobile(false);
    } else {
      setOpen(false);
    }
  };

  const utils = communityTrpc.useUtils();

  const { data: pinnedData, isLoading: pinnedLoading } =
    communityTrpc.pinnedSpaces.list.useQuery();

  const pinnedSpaces = pinnedData?.pinnedSpaces ?? [];

  const reorderMutation = communityTrpc.pinnedSpaces.reorder.useMutation({
    onSuccess: () => {
      utils.pinnedSpaces.list.invalidate();
      toast({ title: t('sidebar.reorderSuccess') });
    },
    onError: () => {
      utils.pinnedSpaces.list.invalidate();
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = pinnedSpaces.findIndex((s) => s.spaceId === active.id);
    const newIndex = pinnedSpaces.findIndex((s) => s.spaceId === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(pinnedSpaces, oldIndex, newIndex);

    utils.pinnedSpaces.list.setData(undefined, {
      pinnedSpaces: reordered.map((s, i) => ({ ...s, position: i + 1 })),
    });

    reorderMutation.mutate({
      orderedSpaceIds: reordered.map((s) => s.spaceId),
    });
  }

  return (
    <Sidebar collapsible="offcanvas">
      <SidebarHeader className="flex items-center justify-between gap-3 p-4">
        <Link to="/" className="flex items-center gap-2.5" onClick={closeSidebar}>
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#cf2e2e] shadow-sm">
            <img src="/icon-hand-small-white.png" alt="" aria-hidden="true" className="h-5 w-5 object-contain" />
          </div>
          <span className="text-base font-semibold text-gray-900">English2Empower</span>
        </Link>
        <SidebarTrigger className="text-primary" />
      </SidebarHeader>

      <SidebarContent>
        {/* Primary navigation */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location.pathname === '/'}
                  className="rounded-lg"
                >
                  <Link to="/" onClick={closeSidebar} aria-current={location.pathname === '/' ? 'page' : undefined}>
                    <Home />
                    <span>{t('nav.home')}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location.pathname.startsWith('/members')}
                  className="rounded-lg"
                >
                  <Link to="/members" onClick={closeSidebar} aria-current={location.pathname.startsWith('/members') ? 'page' : undefined}>
                    <Users />
                    <span>{t('sidebar.members')}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator />

        {/* Pinned Spaces */}
        <SidebarGroup>
          <SidebarGroupLabel>{t('sidebar.pinnedSpaces')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {pinnedLoading && (
                <>
                  <SidebarMenuItem><SidebarMenuSkeleton /></SidebarMenuItem>
                  <SidebarMenuItem><SidebarMenuSkeleton /></SidebarMenuItem>
                  <SidebarMenuItem><SidebarMenuSkeleton /></SidebarMenuItem>
                </>
              )}

              {!pinnedLoading && pinnedSpaces.length === 0 && (
                <li className="px-3 py-2 text-sm text-muted-foreground">
                  {t('sidebar.noPinnedSpaces')}
                </li>
              )}

              {!pinnedLoading && pinnedSpaces.length > 0 && (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={pinnedSpaces.map((s) => s.spaceId)}
                    strategy={verticalListSortingStrategy}
                  >
                    {pinnedSpaces.map((space) => (
                      <PinnedSpaceItem
                        key={space.spaceId}
                        space={space}
                        currentPath={location.pathname}
                        onNavigate={closeSidebar}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}

              {/* Find more spaces */}
              <SidebarMenuItem>
                <SidebarMenuButton asChild className="rounded-lg">
                  <Link to="/spaces" onClick={closeSidebar}>
                    <Compass />
                    <span>{t('sidebar.findMoreSpaces')}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Admin */}
        {isAdmin && (
          <>
            <SidebarSeparator />
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={location.pathname.startsWith('/admin')}
                      className="rounded-lg"
                    >
                      <Link to="/admin" onClick={closeSidebar} aria-current={location.pathname.startsWith('/admin') ? 'page' : undefined}>
                        <Shield />
                        <span>{t('sidebar.admin')}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
      </SidebarContent>

      <SidebarFooter className="p-3">
        <SidebarLanguageToggle />
      </SidebarFooter>
      <SidebarRail className="hidden md:flex" />
    </Sidebar>
  );
}

interface PinnedSpace {
  spaceId: string;
  name: string;
  slug: string;
  subPages: Array<{
    subPageId: string;
    title: string;
    position: number;
  }>;
}

function PinnedSpaceItem({
  space,
  currentPath,
  onNavigate,
}: {
  space: PinnedSpace;
  currentPath: string;
  onNavigate: () => void;
}) {
  const { t } = useTranslation('community');
  const [expanded, setExpanded] = useState(false);
  const spacePath = `/spaces/${space.spaceId}`;
  const isActive =
    currentPath === spacePath || currentPath.startsWith(`${spacePath}/`);
  const hasSubPages = space.subPages.length > 0;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: space.spaceId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <SidebarMenuItem
      ref={setNodeRef}
      style={style}
      className={isDragging ? 'z-10 opacity-80' : ''}
    >
      <SidebarMenuButton
        asChild
        isActive={isActive}
        className="group/space rounded-lg"
      >
        <Link to={spacePath} onClick={onNavigate} aria-current={isActive ? 'page' : undefined}>
          <button
            type="button"
            className="cursor-grab touch-none text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover/space:opacity-100 focus:opacity-100"
            aria-label={t('sidebar.dragHandle')}
            {...attributes}
            {...listeners}
            onClick={(e) => e.preventDefault()}
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
          <span className="truncate">{space.name}</span>
        </Link>
      </SidebarMenuButton>

      {hasSubPages && (
        <button
          type="button"
          className="absolute right-1 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground transition-colors hover:text-foreground"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          aria-label={expanded ? t('sidebar.collapse') : t('sidebar.expand')}
        >
          <ChevronRight
            className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
        </button>
      )}

      {hasSubPages && expanded && (
        <SidebarMenuSub>
          {space.subPages.map((page) => {
            const pagePath = `/spaces/${space.spaceId}/pages/${page.subPageId}`;
            return (
              <SidebarMenuSubItem key={page.subPageId}>
                <SidebarMenuSubButton
                  asChild
                  isActive={currentPath === pagePath}
                >
                  <Link to={pagePath} onClick={onNavigate} aria-current={currentPath === pagePath ? 'page' : undefined}>
                    <span className="truncate">{page.title}</span>
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            );
          })}
        </SidebarMenuSub>
      )}
    </SidebarMenuItem>
  );
}
